/**
 * System health service for provider-level health monitoring.
 *
 * Fetches from GET /api/v1/system/health (public, no auth required).
 * Uses raw axios to avoid auth-interceptor issues on login page.
 */
import axios from 'axios';

// ── Enums ────────────────────────────────────────────────────────────

export type ServiceStatus = 'healthy' | 'degraded' | 'down' | 'not_configured' | 'unknown';
export type ServiceModuleKey = 'stt' | 'llm' | 'report' | 'security' | 'infrastructure' | 'streaming';

// ── Provider-level detail ────────────────────────────────────────────

export interface ProviderHealth {
  name: string;
  module: ServiceModuleKey;
  provider: string | null;
  configured: boolean;
  reachable: boolean;
  active: boolean;
  status: ServiceStatus;
  last_check_timestamp: string | null;
  latency_ms: number | null;
  error_message: string | null;
  metadata: Record<string, unknown>;
}

// ── Module-level detail ──────────────────────────────────────────────

export interface ModuleHealth {
  status: ServiceStatus;
  active_provider: string | null;
  providers: ProviderHealth[];
  warning: string | null;
}

// ── Top-level response from /api/v1/system/health ────────────────────

export interface SystemHealthResponse {
  overall_status: ServiceStatus;
  timestamp: string | null;
  module_status_summary: Record<ServiceModuleKey, ServiceStatus>;
  modules: Record<string, ModuleHealth>;
  degraded_services: string[];
  down_services: string[];
  warnings: string[];
}

// ── Human-readable labels ────────────────────────────────────────────

export const MODULE_LABELS: Record<string, string> = {
  stt: 'Speech-to-Text',
  llm: 'LLM Correction',
  report: 'Report Generation',
  security: 'Security',
  infrastructure: 'Infrastructure',
  streaming: 'Streaming',
};

// ── Helpers ──────────────────────────────────────────────────────────

/** Color for MUI SvgIcon (FiberManualRecordIcon etc.) */
export function statusIconColor(s: ServiceStatus): 'success' | 'warning' | 'error' | 'disabled' {
  switch (s) {
    case 'healthy': return 'success';
    case 'degraded': return 'warning';
    case 'down': return 'error';
    default: return 'disabled';
  }
}

/** Color for MUI Chip */
export function statusColor(s: ServiceStatus): 'success' | 'warning' | 'error' | 'default' {
  switch (s) {
    case 'healthy': return 'success';
    case 'degraded': return 'warning';
    case 'down': return 'error';
    default: return 'default';
  }
}

export function statusLabel(s: ServiceStatus): string {
  switch (s) {
    case 'healthy': return 'Healthy';
    case 'degraded': return 'Degraded';
    case 'down': return 'Down';
    case 'not_configured': return 'Not Configured';
    default: return 'Unknown';
  }
}

// ── Defensive parsing ────────────────────────────────────────────────

const EMPTY_RESPONSE: SystemHealthResponse = {
  overall_status: 'unknown',
  timestamp: null,
  module_status_summary: {} as Record<ServiceModuleKey, ServiceStatus>,
  modules: {},
  degraded_services: [],
  down_services: [],
  warnings: ['Health data unavailable'],
};

/**
 * Validate and normalize the raw API response into a safe SystemHealthResponse.
 * Handles: string responses, missing fields, malformed JSON, legacy schemas.
 */
function parseHealthResponse(raw: unknown): SystemHealthResponse {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    console.warn('[systemHealth] Expected object response, got:', typeof raw);
    return { ...EMPTY_RESPONSE, warnings: [`Unexpected response type: ${typeof raw}`] };
  }

  const data = raw as Record<string, unknown>;

  // Normalize overall_status — backend uses 'overall_status', legacy used 'status'
  const overallStatus: ServiceStatus =
    (typeof data.overall_status === 'string' ? data.overall_status as ServiceStatus : null)
    ?? (typeof data.status === 'string' ? data.status as ServiceStatus : null)
    ?? 'unknown';

  // Normalize modules — must be a dict of {key: ModuleHealth}, not a list or string
  const modules: Record<string, ModuleHealth> = {};
  if (typeof data.modules === 'object' && data.modules !== null && !Array.isArray(data.modules)) {
    const rawModules = data.modules as Record<string, unknown>;
    for (const [key, val] of Object.entries(rawModules)) {
      if (typeof val === 'object' && val !== null) {
        const mod = val as Record<string, unknown>;
        modules[key] = {
          status: (typeof mod.status === 'string' ? mod.status : 'unknown') as ServiceStatus,
          active_provider: typeof mod.active_provider === 'string' ? mod.active_provider : null,
          providers: Array.isArray(mod.providers) ? mod.providers as ProviderHealth[] : [],
          warning: typeof mod.warning === 'string' ? mod.warning : null,
        };
      }
    }
  }

  return {
    overall_status: overallStatus,
    timestamp: typeof data.timestamp === 'string' ? data.timestamp : null,
    module_status_summary:
      (typeof data.module_status_summary === 'object' && data.module_status_summary !== null
        ? data.module_status_summary
        : {}) as Record<ServiceModuleKey, ServiceStatus>,
    modules,
    degraded_services: Array.isArray(data.degraded_services) ? data.degraded_services as string[] : [],
    down_services: Array.isArray(data.down_services) ? data.down_services as string[] : [],
    warnings: Array.isArray(data.warnings) ? data.warnings as string[] : [],
  };
}

// ── Service ──────────────────────────────────────────────────────────

const fetchSystemHealth = async (): Promise<SystemHealthResponse> => {
  const resp = await axios.get('/api/v1/system/health');
  const parsed = parseHealthResponse(resp.data);
  if (process.env.NODE_ENV === 'development') {
    console.debug('[systemHealth] raw:', resp.data, '→ parsed:', parsed);
  }
  return parsed;
};

export { parseHealthResponse, EMPTY_RESPONSE };
export default { fetchSystemHealth };
